import { Arrange, Box, Link, Typography } from "@fullscript/aviary-web";
import { Trans, useTranslation } from "react-i18next";

import oidcSignUpAsset from "@shared/assets/academy-sign-up-asset.png";
import oidcSignUpMobileAsset from "@shared/assets/academy-sign-up-mobile-asset.png";
import * as dimensions from "@styles/emotion-styles/dimensions";
import { SSOErrorBox } from "@unauthenticated/shared/components/SSOErrorBox/SSOErrorBox";
import { SsoErrorPage } from "@unauthenticated/shared/components/SSOErrorBox/types/ssoErrorPage";
import { l } from "@unauthenticated/shared/locales/i18n";

import { OidcSignUpForm } from "./OidcSignUpForm/OidcSignUpForm";

import * as styles from "./OAuthOidcSignUpStep.styles";

const OIDC_LEARN_MORE_URL = "https://fullscript.com/lp/academy-fullscript";
const MOBILE_HERO_MEDIA = `(max-width: ${dimensions.phoneMax})`;

interface Props {
  clientId: string;
  ssoErrors?: string;
  onSignUpComplete: () => void;
  onSignInRedirect: (email?: string) => void;
}

const OAuthOidcSignUpStep = ({
  clientId,
  ssoErrors,
  onSignUpComplete,
  onSignInRedirect,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Arrange
      isVertical
      alignment="center"
      gap="xlarge"
      isFullWidth
      data-testid="oidc-sign-up"
      customCss={styles.wrapper}
    >
      {!!ssoErrors && (
        <SSOErrorBox
          ssoErrorPage={SsoErrorPage.OAUTH}
          ssoErrors={ssoErrors}
          onRedirect={() => onSignInRedirect()}
        />
      )}
      <Arrange isVertical alignment="center" gap="medium" customCss={styles.heading}>
        <Typography variant="h1" isMarginless customCss={styles.headingTitle}>
          {t(l.oauthSignUp.heading)}
        </Typography>
        <Typography variant="body" isMarginless customCss={styles.subheading}>
          {t(l.oauthSignUp.subheading)}
        </Typography>
      </Arrange>
      <Box
        variant="bordered"
        padding="xsmall"
        customCss={styles.marketingCard}
        aria-label={t(l.oauthSignUp.heroAlt)}
      >
        <Arrange alignment="center" gap="large" customCss={styles.marketingInner}>
          <picture css={styles.heroPicture}>
            <source media={MOBILE_HERO_MEDIA} srcSet={oidcSignUpMobileAsset} />
            <img src={oidcSignUpAsset} alt="" css={styles.heroImage} aria-hidden />
          </picture>
          <Arrange isVertical gap="xsmall" customCss={styles.marketingCopy}>
            <Typography variant="h5" isMarginless>
              {t(l.oauthSignUp.marketingCard.title)}
            </Typography>
            <Typography variant="footnote" isMarginless>
              <Trans i18nKey={l.oauthSignUp.marketingCard.body} />{" "}
              <Link href={OIDC_LEARN_MORE_URL} hideExternalIcon customCss={styles.inlineLink}>
                {t(l.oauthSignUp.marketingCard.learnMoreLink)}
              </Link>
            </Typography>
          </Arrange>
        </Arrange>
      </Box>
      <OidcSignUpForm
        clientId={clientId}
        onSignUpComplete={onSignUpComplete}
        onSignInRedirect={onSignInRedirect}
      />
    </Arrange>
  );
};

export { OAuthOidcSignUpStep };
